import React, { Fragment, useEffect, useRef, useState } from "react"
import PropTypes from "prop-types"
import {
  useTable,
  useGlobalFilter,
  useAsyncDebounce,
  useSortBy,
  useFilters,
  useExpanded,
  usePagination,
} from "react-table"
import { Table, Row, Col, Button, Input, Label, Spinner } from "reactstrap"
import { Filter, DefaultColumnFilter } from "./filters"
import { CSVLink } from "react-csv"
import ReactToPrint from "react-to-print"

import PrintDataTable from "./PrintDataTable"

// Define a default UI for filtering
function GlobalFilter({
  preGlobalFilteredRows,
  globalFilter,
  setGlobalFilter,
}) {
  const count = preGlobalFilteredRows.length
  const [value, setValue] = React.useState(globalFilter)
  const onChange = useAsyncDebounce(value => {
    setGlobalFilter(value || undefined)
  }, 200)

  return (
    <Col sm={4}>
      <div className="search-box me-2 d-inline-block">
        <div className="position-relative">
          <label htmlFor="search-bar-0" className="search-label">
            <span id="search-bar-0-label" className="sr-only">
              Search this table
            </span>
            <input
              onChange={e => {
                setValue(e.target.value)
                onChange(e.target.value)
              }}
              id="search-bar-0"
              type="text"
              className="form-control form-control mb-3"
              placeholder={`${count} records...`}
              value={value || ""}
            />
          </label>
          <i className="bx bx-search-alt search-icon katon-search-icon"></i>
        </div>
      </div>
    </Col>
  )
}

const TableContainer = ({
  columns,
  data,
  isGlobalFilter,
  isAddOptions,
  addButtonLabel,
  extraButtons,
  isAddUserList,
  handleOrderClicks,
  handleAddButtonClick,
  handleUserClick,
  handleCustomerClick,
  isAddCustList,
  customPageSize,
  className,
  customPageSizeOptions,
  csvFileName,
  csvData,
  canExportCsv,
  canPrint,
  printColumns,
  dataFetchLoading,
  noDataMessage,
  isPagination = true,
}) => {
  const ref = useRef(null)

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    rows,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state,
    preGlobalFilteredRows,
    setGlobalFilter,

    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data,
      defaultColumn: { Filter: DefaultColumnFilter },
      initialState: {
        pageIndex: 0,
        pageSize: isPagination ? (customPageSize || 10) : 10000,
        sortBy: [
          {
            id: "userId.userName",
            desc: false,
          },
        ],
        hiddenColumns: [],
      },
    },
    useGlobalFilter,
    useFilters,
    useSortBy,
    useExpanded,
    usePagination
  )

  const generateSortingIndicator = column => {
    return column.isSorted ? (column.isSortedDesc ? "🔽" : " 🔼") : ""
  }

  const onChangeInSelect = event => {
    setPageSize(Number(event.target.value))
  }

  const onChangeInInput = event => {
    const page = event.target.value ? Number(event.target.value) - 1 : 0
    gotoPage(page)
  }
  return (
    <Fragment>
      <Row>
        <Col md={customPageSizeOptions ? 2 : 1}>
          <select
            className="form-select form-select"
            value={pageSize}
            onChange={onChangeInSelect}
          >
            {[10, 20, 30, 40, 50].map(pageSize => (
              <option key={pageSize} value={pageSize}>
                Show {pageSize}
              </option>
            ))}
          </select>
        </Col>
        {isGlobalFilter && (
          <GlobalFilter
            preGlobalFilteredRows={preGlobalFilteredRows}
            globalFilter={state.globalFilter}
            setGlobalFilter={setGlobalFilter}
          />
        )}
        {isAddOptions && (
          <Col sm="7">
            <div className="text-sm-end">
              {extraButtons}
              {addButtonLabel && (
                <>
                  <Button
                    type="button"
                    color="success"
                    className="btn mb-2 me-2"
                    onClick={handleAddButtonClick}
                  >
                    {addButtonLabel && (
                      <>
                        <i className="mdi mdi-plus me-1" />
                        {addButtonLabel}
                      </>
                    )}
                  </Button>
                </>
              )}

              {canExportCsv && (
                <>
                  <Button
                    type="button"
                    color="primary"
                    className="btn ml-2 me-2 mb-2"
                    disabled={!rows.length}
                  >
                    <CSVLink
                      filename={csvFileName}
                      data={csvData ? csvData : rows.map(row => row.original)}
                      className="csv-export-button"
                    >
                      <i className="mdi mdi-export-variant me-1" />
                      Export CSV
                    </CSVLink>
                  </Button>
                </>
              )}
              {canPrint && (
                <>
                  <ReactToPrint
                    trigger={() => (
                      <Button
                        type="button"
                        color="warning"
                        className="btn ml-2 mb-2 me-2"
                        disabled={!rows.length}
                      >
                        <i className="mdi mdi-printer me-1" />
                        Print
                      </Button>
                    )}
                    content={() => ref.current}
                  />
                  {printColumns?.length && (
                    <div hidden>
                      <PrintDataTable
                        printColumns={printColumns}
                        printData={rows.map(row => row.original)}
                        ref={ref}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          </Col>
        )}
        {isAddUserList && (
          <Col sm="7">
            <div className="text-sm-end">
              <Button
                type="button"
                color="primary"
                className="btn mb-2 me-2"
                onClick={handleUserClick}
              >
                <i className="mdi mdi-plus-circle-outline me-1" />
                Create New User
              </Button>
            </div>
          </Col>
        )}
        {isAddCustList && (
          <Col sm="7">
            <div className="text-sm-end">
              <Button
                type="button"
                color="success"
                className="btn-rounded mb-2 me-2"
                onClick={handleCustomerClick}
              >
                <i className="mdi mdi-plus me-1" />
                New Customers
              </Button>
            </div>
          </Col>
        )}
      </Row>

      <div className="table-responsive react-table">
        <Table
          bordered
          hover
          {...getTableProps()}
          className={className + " billSaathi-table-container"}
        >
          <thead className="table-light table-nowrap">
            {headerGroups.map(headerGroup => (
              <tr key={headerGroup.id} {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map(column => (
                  <th key={column.id}>
                    <div {...column.getSortByToggleProps()}>
                      {column.render("Header")}
                      {/* {generateSortingIndicator(column)} */}
                    </div>
                    <Filter column={column} />
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody {...getTableBodyProps()}>
            {page.map(row => {
              prepareRow(row)
              return (
                <Fragment key={row.getRowProps().key}>
                  <tr
                    style={(row.original?.invoiceStatus === "cancelled" || row.original?.isInvoiceCancelled)
                      ? { backgroundColor: "#f8d7da" }
                      : {}}
                  >
                    {row.cells.map(cell => {
                      return (
                        <td
                          key={cell.id}
                          {...cell.getCellProps()}
                          style={{
                            textAlign: "center",
                            verticalAlign: "middle",
                          }}
                        >
                          {cell.render("Cell")}
                        </td>
                      )
                    })}
                  </tr>
                </Fragment>
              )
            })}
          </tbody>
        </Table>
      </div>

      {dataFetchLoading ? (
        <>
          <div className="text-center">
            <Spinner color="info" type="grow" />
          </div>
        </>
      ) : (
        <>
          {page?.length === 0 && (
            <>
              <div className="text-center">
                <Label tag="h5">{noDataMessage || "No Data Available"}</Label>
              </div>
            </>
          )}
        </>
      )}

      {isPagination && (
        <Row className="justify-content-md-end justify-content-center align-items-center">
        <Col className="col-md-auto">
          Total: <strong>{preGlobalFilteredRows.length}</strong>
        </Col>
        <Col className="col-md-auto">
          <div className="d-flex gap-1">
            <Button
              color="primary"
              onClick={() => gotoPage(0)}
              disabled={!canPreviousPage}
            >
              {"<<"}
            </Button>
            <Button
              color="primary"
              onClick={previousPage}
              disabled={!canPreviousPage}
            >
              {"<"}
            </Button>
          </div>
        </Col>
        <Col className="col-sm-auto d-none d-md-block">
          Page{" "}
          <strong>
            {pageIndex + 1} of {pageOptions.length}
          </strong>
        </Col>
        <Col className="col-md-auto">
          <Input
            type="number"
            min={1}
            style={{ width: 70 }}
            max={pageOptions.length}
            value={pageIndex + 1}
            onChange={onChangeInInput}
          />
        </Col>

        <Col className="col-sm-auto">
          <div className="d-flex gap-1">
            <Button color="primary" onClick={nextPage} disabled={!canNextPage}>
              {">"}
            </Button>
            <Button
              color="primary"
              onClick={() => gotoPage(pageCount - 1)}
              disabled={!canNextPage}
            >
              {">>"}
            </Button>
          </div>
        </Col>
      </Row>
      )}
    </Fragment>
  )
}

TableContainer.propTypes = {
  preGlobalFilteredRows: PropTypes.any,
}

export default TableContainer
